import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Sidebar from '../components/Sidebar';
import { SnackbarProvider } from 'notistack';
import ToastBridge from '../components/ToastBridge';
import { useUser } from '../hooks/useUser';
import { useNavigation } from '../hooks/useNavigation';
import { AppThemeProvider } from '../theme';
import { PatientProvider } from '../contexts/PatientContext';
import { TreatmentPlanProvider, useTreatmentPlans } from '../contexts/TreatmentPlanContext';
import { RootState } from '../redux/store';
import SelectPatient from '../pages/Clinic/SelectPatient';
import CreateWithAiOptions from '../pages/Clinic/TreatmentPlan/CreateWithAi/Options';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('../pages/Clinic/Dashboard/page'));
const Patients = lazy(() => import('../pages/Patients'));
const AddPatient = lazy(() => import('../pages/AddPatient'));
const PatientDetails = lazy(
  () => import('../pages/Patients/components/PatientDetailsModal/PatientDetails')
);
const Templates = lazy(() => import('../pages/Clinic/Templates'));
const Drafts = lazy(() => import('../pages/Clinic/Drafts'));
const TreatmentPlan = lazy(() => import('../pages/Clinic/TreatmentPlan/page'));
const TreatmentPlanCreation = lazy(() => import('../pages/TreatmentPlanCreation'));
const MediaLibrary = lazy(() => import('../pages/MediaLibrary'));
const Settings = lazy(() => import('../pages/Clinic/Settings'));
const LoginPage = lazy(() => import('../pages/Auth/Login'));
const RegisterPage = lazy(() => import('../pages/Auth/Register'));
const VerifyEmailPage = lazy(() => import('../pages/Auth/VerifyEmail'));
const SetupClinicPage = lazy(() => import('../pages/Auth/SetupClinic'));
const ActivatePage = lazy(() => import('../pages/Auth/activate'));
const ForgotPasswordPage = lazy(() => import('../pages/Auth/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('../pages/Auth/ResetPassword'));
const UserTypeSelection = lazy(() => import('../pages/UserTypeSelection'));
// Loading component
const LoadingSpinner = () => (
  <div className="flex h-screen items-center justify-center bg-gray-100 dark:bg-gray-900">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Layout component that includes the sidebar and providers
const AppLayout = () => {
  const currentPath = useLocation()?.pathname;
  return (
    <AppThemeProvider>
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <ToastBridge />
        <PatientProvider>
          <TreatmentPlanProvider>
            <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
              <Sidebar />
              <main className={`flex-1 ${currentPath === '/' ? '' : 'overflow-auto'}`}>
                <div className="mx-auto">
                  <Suspense fallback={<LoadingSpinner />}>
                    <Outlet />
                  </Suspense>
                </div>
              </main>
            </div>
          </TreatmentPlanProvider>
        </PatientProvider>
      </SnackbarProvider>
    </AppThemeProvider>
  );
};

// Auth guard component - protects routes that require authentication
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading, refreshToken } = useUser();
  const { tokens } = useSelector((state: RootState) => state.auth);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState(false);

  useEffect(() => {
    const attemptRefresh = async () => {
      // If not authenticated but we have tokens, try to refresh
      if (!isAuthenticated() && tokens?.refresh && !hasAttemptedRefresh) {
        setIsRefreshing(true);
        setHasAttemptedRefresh(true);
        try {
          await refreshToken();
        } catch (error) {
          console.error('Failed to refresh token in AuthGuard:', error);
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    attemptRefresh();
  }, [isAuthenticated, tokens, refreshToken, hasAttemptedRefresh]);

  if (loading || isRefreshing) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Guest guard component - protects routes that should only be accessible to non-authenticated users
const GuestGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useUser();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Navigation wrapper components
const DashboardWrapper = () => {
  const { goTo } = useNavigation();
  return <Dashboard onNavigate={page => goTo(page)} />;
};

const PatientsWrapper = () => {
  const { goTo } = useNavigation();
  return <Patients onNavigate={goTo} />;
};

const TemplatesWrapper = () => {
  return <Templates />;
};

const DraftsWrapper = () => {
  const { goTo } = useNavigation();
  return <Drafts onNavigate={goTo} />;
};

const TreatmentPlanCreationWrapper = () => {
  const { goTo } = useNavigation();
  const { resetTreatmentPlan } = useTreatmentPlans();
  useEffect(() => {
    resetTreatmentPlan();
  }, [resetTreatmentPlan]);
  return <TreatmentPlanCreation onNavigate={goTo} />;
};

const MediaLibraryWrapper = () => {
  const { goTo } = useNavigation();
  return <MediaLibrary onNavigate={goTo} />;
};

const PatientDetailsWrapper = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();

  const handleClose = () => {
    navigate('/dashboard/patients');
  };

  if (!patientId) {
    return <div>Patient ID not found</div>;
  }

  return <PatientDetails patientId={patientId} onClose={handleClose} />;
};

// Create the router configuration
export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <UserTypeSelection />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: '/login',
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <LoginPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: '/activate/:uid/',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <ActivatePage />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <RegisterPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <ForgotPasswordPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: '/reset/:token',
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <ResetPasswordPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <VerifyEmailPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: '/setup-clinic',
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingSpinner />}>
          <SetupClinicPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <DashboardWrapper />,
      },
      {
        path: 'patients',
        children: [
          {
            index: true,
            element: <PatientsWrapper />,
          },
          {
            path: 'add',
            element: <AddPatient />,
          },
          {
            path: ':patientId',
            element: <PatientDetailsWrapper />,
          },
        ],
      },
      {
        path: 'templates',
        element: <TemplatesWrapper />,
      },
      {
        path: 'drafts',
        element: <DraftsWrapper />,
      },
      {
        path: 'treatment-plan',
        children: [
          {
            index: true,
            element: <TreatmentPlan />,
          },
          {
            path: 'create',
            element: <TreatmentPlanCreationWrapper />,
          },
          {
            path: 'select-patient',
            element: <SelectPatient />,
          },
          {
            path: 'create-with-ai-options',
            element: <CreateWithAiOptions />,
          },
        ],
      },
      {
        path: 'media-library',
        element: <MediaLibraryWrapper />,
      },
      {
        path: 'settings',
        element: <Settings />,
      },
    ],
  },
]);
